import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { Ng2LinkComponent } from './ng2-link/ng2-link.component';
import { RouterModule } from '@angular/router';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NotificationService } from './notification.service';
import {
  MatButtonModule,
  MatCheckboxModule,
  MatChipsModule,
  MatDialogModule,
  MatIconModule,
  MatRadioModule,
  MatSelectModule,
  MatSnackBarModule,
  MatTableModule,
} from '@angular/material';
import { TranslateModule } from '@ngx-translate/core';
import { localStorageSyncReducer } from './auth/local-storage.meta.reducer';
import { AuthEffects } from './auth/auth.effects';
import { reducer } from './auth/auth.reducer';
import { DataSourceUtils } from './utils/data-source-utils';
import { CcDatePipe } from './cc-date.pipe';
import { Daterangepicker } from 'ng2-daterangepicker';
import { userReducer } from './user/user.reducer';
import { UserEffects } from './user/user.effects';
import { NgxPermissionsModule } from 'ngx-permissions';
import { InputErrorsComponent } from './input-errors/input-errors.component';
import { InputErrorComponent } from './input-errors/input-error/input-error.component';
import { LineChartComponent } from './charts/line/line-chart.component';
import { ChartsModule } from 'ng2-charts';
import { LineChartService } from './charts/line/line-chart.service';
import { DoughnutChartComponent } from './charts/doughnut/doughnut-chart.component';
import { DoughnutChartService } from './charts/doughnut/doughnut-chart.service';
import { EmptyChartComponent } from './charts/empty/empty-chart.component';
import { Ng1LoadedGuard } from './ng1/ng1-loaded.guard';
import { RequestUtils } from './utils/request';
import { MsisdnComponent } from './msisdn/msisdn.component';
import { MsisdnService } from './msisdn/msisdn.service';
import { Ng1IpInfoService } from './ng1/ng1-ip-info.service';
import { AmplitudeService } from './services/amplitude.service';
import { DataLayerService } from './services/data-layer.service';
import { Ng1FileDropDirective, Ng1FileDropPlayerDirective } from './ng1/ng1-file-drop';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TimepickerComponent } from './timepicker/timepicker.component';
import { LanguageSelectorComponent } from './language-selector/language-selector.component';
import { TypeformService } from './services/typeform.service';
import { VerifyPhoneNumberService } from './verify-phone-number/verify-phone-number.service';
import { VerifyCodeComponent } from './verify-phone-number/verify-code/verify-code.component';
import { AudioMessageUploadComponent } from './audio-message-upload/audio-message-upload.component';
import { TimeFromNowPipe } from './pipes/time-from-now.pipe';
import { TooltipIconComponent } from './tooltip-icon/tooltip-icon.component';
import { TimezonesDropdownComponent } from '../lead-generation/shared/timezones-dropdown/timezones-dropdown.component';
import { DropdownInputComponent } from '../lead-generation/shared/dropdown-input/dropdown-input.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReactiveFormsModule } from '@angular/forms';
import { WorkingHoursListComponent } from './working-hours/working-hours-list.component';
import { WorkingHoursRangeComponent } from './working-hours/working-hours-range/working-hours-range.component';
import { DaysRangeComponent } from './working-hours/working-hours-range/days-range/days-range.component';
import { UiSwitchModule } from 'ngx-ui-switch';
import { UI_SWITCH_CONFIGURATION } from './utils/layoutUtils';
import { LayoutModule } from './layout/layout.module';
import { PhoneNumberRowComponent } from '../campaign-settings/phone-numbers/phone-number-row/phone-number-row.component';
import { MsisdnResolver } from './msisdn/msisdn.resolver';
import { IntlPhoneNumberInputComponent } from './intl-phone-number-input/intl-phone-number-input.component';
import { ScrollToFirstInvalidFieldDirective } from '../lead-generation/shared/scroll-to-first-invalid-field/scroll-to-first-invalid-field.directive';
import { ConfirmDialogComponent } from './dialog/confirm-dialog/confirm-dialog.component';
import { InfoDialogComponent } from './dialog/info-dialog/info-dialog.component';
import { Ng1LiveChatService } from './ng1/ng1-live-chat.service';
import { StripeApiKeyInjector } from './StripeApiKeyInjector';
import { SubscriptionCheckoutService } from '../account/subscription-checkout.service';
import { PaymentService } from './services/payment.service';
import { Ng1WebrtcUtilsService } from './ng1/ng1-webrtc-utils.service';
import { InputLabelErrorWrapperComponent } from './input-label-error-wrapper/input-label-error-wrapper.component';
import { LabelValueComponent } from './label-value/label-value.component';
import { CountryPickerComponent } from './country-picker/country-picker.component';
import { MsisdnDropdownComponent } from './msisdn/msisdn-dropdown/msisdn-dropdown.component';
import { MsisdnDropdownItemComponent } from './msisdn/msisdn-dropdown/msisdn-dropdown-item/msisdn-dropdown-item.component';
import { Ng1ModalService } from './ng1/ng1-modal.service';
import { ConfirmComponent } from './confirm/confirm.component';
import { MsisdnCheckboxListComponent } from './msisdn/msisdn-checkbox-list/msisdn-checkbox-list.component';
import { MsisdnListComponent } from './msisdn/msisdn-list/msisdn-list.component';
import { ItemMsisdnComponent } from './msisdn/msisdn-list/item-msisdn/item-msisdn.component';
import { limitsReducer } from 'app/shared/limits/limits.reducer';
import { LimitsEffects } from 'app/shared/limits/limits.effects';
import { CountriesDropdownComponent } from './countries-dropdown/countries-dropdown.component';
import { PhoneNumberPipe } from './pipes/phone-number.pipe';
import { DebounceValueChangeDirective } from './debounce-value-change/debounce-value-change.directive';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { DetailsViewComponent } from 'app/shared/details-view/details-view.component';
import { Ng1PricingDirective } from './ng1/ng1-pricing.directive';
import { Ng1PhoneNumberSettingsListDirective } from './ng1/ng1-phone-number-settings-list.directive';
import { Ng1PhoneNumberSettingsDirective } from './ng1/ng1-phone-number-settings.directive';
import { Ng1CustomPhoneNumberDirective } from './ng1/ng1-custom-phone-number.directive';
import { Ng1UserListDirective } from './ng1/ng1-user-list.directive';
import { Ng1UserDetailsDirective } from './ng1/ng1-user-details.directive';
import { Ng1UserEditDirective } from './ng1/ng1-user-edit.directive';
import { Ng1UserExistsInviteDirective } from './ng1/ng1-user-exists-invite.directive';
import { Ng1UserNewInviteDirective } from './ng1/ng1-user-new-invite.directive';
import { Ng1UsersAlertDirective } from './ng1/ng1-users-alert.directive';
import { Ng1ExistsNumberDirective } from './ng1/ng1-exists-number.directive';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { DollarSignComponent } from './dollar-sign/dollar-sign.component';
import { WebWidgetComponent } from './web-widget/web-widget.component';
import { ClickOutsideModule } from 'ng-click-outside';
import { WebWidgetService } from './web-widget/web-widget.service';
import { CallIconComponent } from './call-icon/call-icon.component';
import { MobileStoreButtonsComponent } from './mobile-store-buttons/mobile-store-buttons.component';
import { Ng1MenuTopDirective } from './ng1/ng1-menu-top.directive';
import { DateFilterComponent } from './date-filter/date-filter.component';
import { DatePickerComponent } from './date-picker/date-picker.component';
import { DatePickerService } from './services/date.picker.service';
import { ClickToCallButtonComponent } from './click-to-call-button/click-to-call-button.component';
import { logger } from './logger/logger';
import { environment } from '../../environments/environment';
import { DateRangePickerComponent } from './date-range-picker/date-range-picker.component';
import { NoWhitespaceDirective } from "./validators/no-whitespace.directive";

const productionMetaReducers = [localStorageSyncReducer];
const devMetaReducers = [...productionMetaReducers, logger];

const components = [
  Ng2LinkComponent,
  CcDatePipe,
  DateRangePickerComponent,
  DateFilterComponent,
  DatePickerComponent,
  InputErrorComponent,
  InputErrorsComponent,
  LineChartComponent,
  DoughnutChartComponent,
  EmptyChartComponent,
  MsisdnComponent,
  TimepickerComponent,
  LanguageSelectorComponent,
  Ng1FileDropDirective,
  Ng1FileDropPlayerDirective,
  Ng1PricingDirective,
  Ng1PhoneNumberSettingsListDirective,
  Ng1PhoneNumberSettingsDirective,
  Ng1CustomPhoneNumberDirective,
  Ng1UserListDirective,
  Ng1UserDetailsDirective,
  Ng1UserEditDirective,
  Ng1UserExistsInviteDirective,
  Ng1UserNewInviteDirective,
  Ng1UsersAlertDirective,
  Ng1ExistsNumberDirective,
  Ng1MenuTopDirective,
  TimepickerComponent,
  VerifyCodeComponent,
  AudioMessageUploadComponent,
  VerifyCodeComponent,
  TooltipIconComponent,
  TimezonesDropdownComponent,
  DropdownInputComponent,
  WorkingHoursListComponent,
  WorkingHoursRangeComponent,
  DaysRangeComponent,
  PhoneNumberRowComponent,
  IntlPhoneNumberInputComponent,
  ScrollToFirstInvalidFieldDirective,
  ConfirmDialogComponent,
  InfoDialogComponent,
  InputLabelErrorWrapperComponent,
  LabelValueComponent,
  CountryPickerComponent,
  MsisdnDropdownComponent,
  MsisdnCheckboxListComponent,
  MsisdnListComponent,
  ItemMsisdnComponent,
  CountriesDropdownComponent,
  DebounceValueChangeDirective,
  CountriesDropdownComponent,
  DetailsViewComponent,
  DollarSignComponent,
  CallIconComponent,
  WebWidgetComponent,
  ClickToCallButtonComponent,
];

const pipes = [TimeFromNowPipe, PhoneNumberPipe, SafeUrlPipe];

const materialModules = [
  MatAutocompleteModule,
  MatSnackBarModule,
  MatButtonModule,
  MatTableModule,
  MatSelectModule,
  MatChipsModule,
  MatIconModule,
  MatDialogModule,
  MatRadioModule,
  MatCheckboxModule,
  MatPaginatorModule,
  MatSortModule,
];

@NgModule({
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    LayoutModule,
    StoreModule.forRoot(
      { auth: reducer, user: userReducer, limits: limitsReducer },
      {
        metaReducers: environment.production ? productionMetaReducers : devMetaReducers,
      }
    ),
    EffectsModule.forRoot([AuthEffects, UserEffects, LimitsEffects]),
    StoreDevtoolsModule.instrument(),
    TranslateModule,
    Daterangepicker,
    ChartsModule,
    UiSwitchModule.forRoot(UI_SWITCH_CONFIGURATION),
    NgxPermissionsModule,
    NgxMatSelectSearchModule,
    ClickOutsideModule,
    ...materialModules,
  ],
  declarations: [
    ...components,
    ...pipes,
    MsisdnDropdownItemComponent,
    ConfirmComponent,
    SafeUrlPipe,
    WebWidgetComponent,
    MobileStoreButtonsComponent,
    ClickToCallButtonComponent,
    NoWhitespaceDirective
  ],
  exports: [
    ...components,
    ...pipes,
    ...materialModules,
    CommonModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    ConfirmComponent,
    DebounceValueChangeDirective,
    MobileStoreButtonsComponent,
    NoWhitespaceDirective
  ],
  providers: [
    Ng1LoadedGuard,
    Ng1IpInfoService,
    Ng1ModalService,
    NotificationService,
    DataSourceUtils,
    LineChartService,
    StripeApiKeyInjector,
    DoughnutChartService,
    RequestUtils,
    AmplitudeService,
    DataLayerService,
    MsisdnService,
    TypeformService,
    AmplitudeService,
    VerifyPhoneNumberService,
    MsisdnResolver,
    Ng1LiveChatService,
    Ng1WebrtcUtilsService,
    SubscriptionCheckoutService,
    PaymentService,
    WebWidgetService,
    DatePickerService,
  ],
  entryComponents: [
    Ng2LinkComponent,
    LineChartComponent,
    DoughnutChartComponent,
    LanguageSelectorComponent,
    AudioMessageUploadComponent,
    ConfirmDialogComponent,
    InfoDialogComponent,
    ConfirmComponent,
    MsisdnCheckboxListComponent,
    MsisdnListComponent,
    CountriesDropdownComponent,
    WebWidgetComponent,
  ],
})
export class SharedModule {}
